import { useMemo } from "react";
import { isSameMonth } from "date-fns";
import { CalendarDays } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";
import { monthGrid, indexByDate, toISO, isoWeek, WEEKDAYS_NN, WEEKDAYS_MIN_NN, worstStatus, dayTitleLong, fromISO } from "@/lib/calendar";
import { cn } from "@/lib/utils";
import { EventChip, EventRow } from "./EventItem";

const MAX_CHIPS = 3;

export const MonthView = ({ anchor, events, today, selected, onSelectDay, onOpenEvent }) => {
  const weeks = useMemo(() => monthGrid(anchor), [anchor]);
  const idx = useMemo(() => indexByDate(events), [events]);

  return (
    <div className="overflow-hidden rounded-2xl border bg-card shadow-sm" data-testid="calendar-month-view">
      {/* Weekday header */}
      <div className="grid grid-cols-[28px_repeat(7,minmax(0,1fr))] border-b bg-muted/50 sm:grid-cols-[44px_repeat(7,minmax(0,1fr))]">
        <div className="px-1 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">V</div>
        {WEEKDAYS_NN.map((d, i) => (
          <div key={d} className={cn("py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground", i >= 5 && "text-muted-foreground/70")}>
            <span className="hidden sm:inline">{d}</span>
            <span className="sm:hidden">{WEEKDAYS_MIN_NN[i]}</span>
          </div>
        ))}
      </div>

      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-[28px_repeat(7,minmax(0,1fr))] border-b last:border-b-0 sm:grid-cols-[44px_repeat(7,minmax(0,1fr))]">
          <div className="flex items-start justify-center border-r bg-muted/30 pt-2 text-[10px] font-semibold tabular-nums text-muted-foreground sm:pt-2.5 sm:text-[11px]">
            {isoWeek(week[0])}
          </div>
          {week.map((day) => {
            const iso = toISO(day);
            const due = idx.due.get(iso) || [];
            const planned = idx.planned.get(iso) || [];
            const inMonth = isSameMonth(day, anchor);
            const isToday = iso === today;
            const isSelected = iso === selected;
            const items = [...due.map((e) => ({ e, planned: false })), ...planned.map((e) => ({ e, planned: true }))];
            const overflow = items.length - MAX_CHIPS;
            const dayWorst = worstStatus(due.map((e) => e.status));
            return (
              <div
                key={iso}
                role="button"
                tabIndex={0}
                onClick={() => onSelectDay(iso)}
                onKeyDown={(ev) => (ev.key === "Enter" || ev.key === " ") && onSelectDay(iso)}
                data-testid={`calendar-day-${iso}`}
                aria-label={`${dayTitleLong(day)}${items.length ? `, ${items.length} hendingar` : ""}`}
                className={cn(
                  "relative min-h-[64px] cursor-pointer border-r p-1 text-left transition-colors last:border-r-0 sm:min-h-[112px] sm:p-1.5",
                  "hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60",
                  !inMonth && "bg-muted/20",
                  isSelected && "bg-primary/[0.06] ring-2 ring-inset ring-primary"
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      "inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold tabular-nums sm:h-7 sm:w-7 sm:text-sm",
                      isToday ? "bg-primary text-ink" : inMonth ? "text-foreground" : "text-muted-foreground/50"
                    )}
                  >
                    {day.getDate()}
                  </span>
                  {items.length > 0 && (
                    <span className="hidden text-[10px] font-semibold tabular-nums text-muted-foreground sm:inline">{items.length}</span>
                  )}
                </div>

                {/* Desktop: chips */}
                <div className="mt-1 hidden space-y-1 sm:block">
                  {items.slice(0, MAX_CHIPS).map(({ e, planned: p }) => (
                    <EventChip
                      key={`${e.id}${p ? "-p" : ""}`}
                      e={e}
                      planned={p}
                      dim={!inMonth}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        onOpenEvent ? onOpenEvent(e, iso) : onSelectDay(iso);
                      }}
                    />
                  ))}
                  {overflow > 0 && (
                    <div className="px-1 text-[11px] font-semibold text-muted-foreground">+{overflow} fleire</div>
                  )}
                </div>

                {/* Mobile: dots */}
                <div className="mt-1 flex flex-wrap gap-0.5 sm:hidden">
                  {due.slice(0, 3).map((e) => (
                    <span key={e.id} className={cn("h-1.5 w-1.5 rounded-full", `cal-fill-${e.status}`)} />
                  ))}
                  {planned.slice(0, 2).map((e) => (
                    <span key={`${e.id}-p`} className="cal-fill-planned h-1.5 w-1.5 rounded-full" />
                  ))}
                  {items.length > 5 && <span className="text-[9px] font-bold leading-none text-muted-foreground">+</span>}
                </div>
                {dayWorst && <span className="sr-only">{dayWorst}</span>}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

/** Detaljar for vald dag (brukt i sidepanel på desktop og i Sheet på mobil). */
export const DayDetails = ({ dateISO, events, today, onPlan, onMove, onUnplan }) => {
  const idx = useMemo(() => indexByDate(events), [events]);
  const due = idx.due.get(dateISO) || [];
  const planned = idx.planned.get(dateISO) || [];
  if (due.length === 0 && planned.length === 0) {
    return (
      <EmptyState
        icon={CalendarDays}
        title="Ingen fristar denne dagen"
        description="Vel ein annan dag, eller planlegg ein jobb hit frå handlingsmenyen på ei frist."
        className="py-8"
      />
    );
  }
  return (
    <div className="space-y-4" data-testid="calendar-day-details">
      {planned.length > 0 && (
        <section className="space-y-2">
          <h4 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <span className="cal-fill-planned inline-block h-2 w-2 rounded-full" /> Planlagt denne dagen
          </h4>
          {planned.map((e) => (
            <EventRow key={`${e.id}-p`} e={e} today={today} mode="planned" compact onPlan={onPlan} onMove={onMove} onUnplan={onUnplan} />
          ))}
        </section>
      )}
      {due.length > 0 && (
        <section className="space-y-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Fristar</h4>
          {due.map((e) => (
            <EventRow key={e.id} e={e} today={today} compact onPlan={onPlan} onMove={onMove} onUnplan={onUnplan} />
          ))}
        </section>
      )}
    </div>
  );
};

export const selectedDayLabel = (iso) => (iso ? dayTitleLong(fromISO(iso)) : "Vel ein dag");
